import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { useTeamChannelStore, type TeamMessage } from '../stores/teamChannelStore';
import { useSettingsStore } from '../stores/settingsStore';
import { gatewayRef } from '../utils/gatewayRef';
import { registerTeamSession, unregisterTeamSession } from '../utils/teamSessionRegistry';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { buildApiUrl } from '../utils/serverOrigin';
import ThinkingIndicator from './ThinkingIndicator';
import ToolCallSection from './ToolCallSection';
import type { PendingAttachment, ToolCallEntry } from '../stores/appStore';
import AgentIconBar from './AgentIconBar';
import AgentReplyPopup from './AgentReplyPopup';
import AgentMeetingView from './AgentMeetingView';
import {
  CHAT_UPLOAD_ACCEPT,
  MAX_CHAT_ATTACHMENTS,
  appendUploadedAttachmentsToMessage,
  createPendingAttachment,
  uploadPendingAttachments,
} from '../utils/chatUploads';
import { getSessionTokenCount, shouldShowContextTokenWarning } from '../utils/contextTokenWarning';
import AttachmentPreviewBar from './AttachmentPreviewBar';
import ContextTokenWarningDialog from './ContextTokenWarningDialog';

type ViewMode = 'normal' | 'meeting';

// ─── CSS-in-JS animation keyframes ───────────────────────────────────────────
const STYLES = `
@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
  30% { transform: translateY(-4px); opacity: 1; }
}
@keyframes mintPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.team-channel-scroll::-webkit-scrollbar { width: 4px; }
.team-channel-scroll::-webkit-scrollbar-track { background: transparent; }
.team-channel-scroll::-webkit-scrollbar-thumb { background: var(--theme-muted); border-radius: 2px; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const AGENT_EMOJIS: Record<string, string> = {
  main: '🔥',
  flint: '🔥',
  yuki: '🌸',
  researcher: '🔬',
  coder: '💻',
};

function getAgentEmoji(agentId: string): string {
  return AGENT_EMOJIS[agentId?.toLowerCase()] ?? '🤖';
}

// ─── Message Components ───────────────────────────────────────────────────────

interface HumanMessageProps {
  message: TeamMessage;
  prevMessage?: TeamMessage;
  maxLength?: number;
}

function HumanMessage({ message, prevMessage }: HumanMessageProps) {
  const isAgentCall = message.type === 'agent_call';
  const initials = getInitials(message.sender);
  const sameAsPrev = prevMessage?.sender === message.sender &&
    prevMessage?.type === message.type &&
    message.ts - (prevMessage?.ts ?? 0) < 2 * 60 * 1000;

  // Parse @mentions to highlight them in agent_call messages
  const renderContent = () => {
    if (!isAgentCall) {
      return (
        <span style={{
          fontFamily: 'var(--app-code-font)',
          fontSize: '0.72rem',
          lineHeight: 1.55,
          color: 'var(--theme-card-foreground)',
        }}>
          {message.content}
        </span>
      );
    }

    const agentName = message.agentName ?? '';
    return (
      <span style={{
        fontFamily: 'var(--app-code-font)',
        fontSize: '0.72rem',
        lineHeight: 1.55,
        color: 'var(--theme-card-foreground)',
      }}>
        {agentName && (
          <span style={{
            color: 'var(--theme-primary)',
            background: 'var(--theme-accent)',
            border: '1px solid var(--theme-primary-20)',
            borderRadius: '3px',
            padding: '0.05em 0.3em',
            fontSize: '0.68rem',
            fontWeight: 700,
            marginRight: '0.4em',
          }}>
            @{agentName}
          </span>
        )}
        {message.content}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
      {/* Avatar */}
      {sameAsPrev ? (
        <div style={{ width: '28px', flexShrink: 0 }} />
      ) : (
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            background: 'var(--theme-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--app-ui-font)',
            fontSize: '0.6rem',
            fontWeight: 700,
            color: 'var(--theme-muted-foreground)',
            flexShrink: 0,
            marginTop: '2px',
          }}
        >
          {initials}
        </div>
      )}

      {/* Bubble */}
      <div
        style={{
          borderRadius: '6px',
          padding: '0.65rem 0.875rem',
          flex: 1,
          background: 'var(--theme-user-bubble-background)',
        }}
      >
        {!sameAsPrev && (
          <div
            style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              marginBottom: '0.3rem',
              color: 'var(--theme-muted-foreground)',
            }}
          >
            {message.sender.toUpperCase()} · {formatTime(message.ts)}
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
}

interface AgentStreamingRowProps {
  agentName: string;
  agentEmoji: string;
  streamText: string;
  maxLength: number;
  thinkingText?: string;
  toolCalls?: ToolCallEntry[];
}

function AgentStreamingRow({ agentName, agentEmoji, streamText, maxLength, thinkingText, toolCalls }: AgentStreamingRowProps) {
  const preview = streamText.slice(0, maxLength);
  const hasThinking = Boolean(thinkingText);
  const hasTools = Boolean(toolCalls && toolCalls.length > 0);

  return (
    <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
      {/* Avatar */}
      <div
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: 'var(--theme-accent)',
          border: '1px solid var(--theme-primary-20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.9rem',
          flexShrink: 0,
          marginTop: '2px',
        }}
      >
        {agentEmoji}
      </div>

      {/* Content column — thinking/tools above bubble, matching ChatPanel live area */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Thinking indicator (above bubble) */}
        {hasThinking && (
          <div style={{ marginBottom: '0.375rem' }}>
            <ThinkingIndicator isThinking={!streamText} content={thinkingText} />
          </div>
        )}

        {/* Tool calls (above bubble) */}
        {hasTools && (
          <div style={{ marginBottom: '0.375rem' }}>
            <ToolCallSection entries={toolCalls!} />
          </div>
        )}

        {/* Streaming text bubble */}
        <div
          style={{
            borderRadius: '6px',
            padding: '0.65rem 0.875rem',
            background: 'var(--theme-assistant-bubble-background)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              marginBottom: '0.3rem',
              color: 'var(--theme-primary)',
            }}
          >
            {agentName.toUpperCase()}
          </div>
          {streamText ? (
            <div
              style={{
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.72rem',
                lineHeight: 1.55,
                color: 'var(--theme-card-foreground)',
                wordBreak: 'break-word',
              }}
            >
              {preview}
              {streamText.length > maxLength && '...'}
              <span
                style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '12px',
                  background: 'var(--theme-primary)',
                  marginLeft: '2px',
                  verticalAlign: 'middle',
                  animation: 'pulse-dot 0.8s infinite',
                }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '2px 0' }}>
              {[0, 0.15, 0.3].map((delay, i) => (
                <div
                  key={i}
                  style={{
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    background: 'var(--theme-primary)',
                    animation: `typingBounce 1.3s ease-in-out ${delay}s infinite`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface AgentReplyRowProps {
  message: TeamMessage;
  maxLength: number;
  onExpand: () => void;
}

function AgentReplyRow({ message, maxLength, onExpand }: AgentReplyRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const agentEmoji = getAgentEmoji(message.agentId ?? 'main');
  const isTruncated = message.content.length > maxLength;
  const preview = isTruncated ? message.content.slice(0, maxLength) : message.content;

  // Convert TeamMessageToolCall[] → ToolCallEntry[] for ToolCallSection
  const toolCallEntries: ToolCallEntry[] | undefined = message.toolCalls && message.toolCalls.length > 0
    ? message.toolCalls.map((tc) => {
        let params: Record<string, unknown> | undefined;
        if (tc.input) {
          try { params = JSON.parse(tc.input); } catch { params = { input: tc.input }; }
        }
        let result: unknown;
        if (tc.output) {
          try { result = JSON.parse(tc.output); } catch { result = tc.output; }
        }
        return {
          toolCallId: tc.toolCallId,
          name: tc.name,
          timestamp: Date.now(),
          status: tc.status,
          params,
          result,
        };
      })
    : undefined;

  return (
    <div
      style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Avatar */}
      <div
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: 'var(--theme-accent)',
          border: '1px solid var(--theme-primary-20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.9rem',
          flexShrink: 0,
          marginTop: '2px',
        }}
      >
        {agentEmoji}
      </div>

      {/* Content column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Committed thinking blocks */}
        {message.thinkingContent && (
          <div style={{ marginBottom: '0.375rem' }}>
            <ThinkingIndicator isThinking={false} content={message.thinkingContent} isCommitted />
          </div>
        )}

        {/* Committed tool calls */}
        {toolCallEntries && toolCallEntries.length > 0 && (
          <div style={{ marginBottom: '0.375rem' }}>
            <ToolCallSection entries={toolCallEntries} isCommitted />
          </div>
        )}

        {/* Message bubble */}
        <div
          style={{
            borderRadius: '6px',
            padding: '0.65rem 0.875rem',
            background: isHovered
              ? 'var(--theme-secondary)'
              : 'var(--theme-assistant-bubble-background)',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
          }}
          onClick={onExpand}
        >
          <div
            style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              marginBottom: '0.3rem',
              color: 'var(--theme-primary)',
            }}
          >
            {(message.agentName ?? message.sender).toUpperCase()}
          </div>

          <div
            style={{
              fontFamily: 'var(--app-code-font)',
              fontSize: '0.72rem',
              lineHeight: 1.55,
              color: 'var(--theme-card-foreground)',
              wordBreak: 'break-word',
            }}
          >
            {preview}
            {isTruncated && (
              <>
                {' '}
                <span
                  style={{
                    color: 'var(--theme-primary)',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  ... View full response →
                </span>
              </>
            )}
          </div>

          {/* Response badge */}
          <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                fontFamily: 'var(--app-ui-font)',
                fontSize: '0.58rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'var(--theme-success)',
                background: 'color-mix(in srgb, var(--theme-success) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--theme-success) 15%, transparent)',
                padding: '0.15rem 0.5rem',
                borderRadius: '3px',
              }}
            >
              ✦ {formatTime(message.ts)}
            </span>
            {isHovered && (
              <span
                style={{
                  fontFamily: 'var(--app-ui-font)',
                  fontSize: '0.55rem',
                  color: 'var(--theme-muted-foreground)',
                }}
              >
                click to expand
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TeamChannelView() {
  const [viewMode, setViewMode] = useState<ViewMode>('normal');
  const agents = useAppStore((state) => state.agents);
  const sessions = useAppStore((state) => state.sessions);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const displayName = useSettingsStore((state) => state.settings.general.displayName);

  const {
    messages,
    isLoading,
    hasMore,
    activeAgentCalls,
    agentReplyMaxLength,
    popupSessionKey,
    loadInitial,
    loadMore,
    pollNew,
    sendMessage,
    openPopup,
  } = useTeamChannelStore();

  const inputValue = useAppStore((state) => state.draftTexts['team'] ?? '');
  const setDraftText = useAppStore((state) => state.setDraftText);
  const setInputValue = useCallback((text: string) => {
    setDraftText('team', text);
  }, [setDraftText]);
  const [isSending, setIsSending] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [pendingTokenWarningText, setPendingTokenWarningText] = useState<string | null>(null);
  const [pendingTokenWarningCount, setPendingTokenWarningCount] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inject CSS
  useEffect(() => {
    const existing = document.getElementById('team-channel-styles');
    if (!existing) {
      const style = document.createElement('style');
      style.id = 'team-channel-styles';
      style.textContent = STYLES;
      document.head.appendChild(style);
    }
    return () => {
      document.getElementById('team-channel-styles')?.remove();
    };
  }, []);

  // Load initial messages
  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  // Setup polling
  useEffect(() => {
    pollTimerRef.current = setInterval(() => {
      void pollNew();
    }, 2000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [pollNew]);

  // Note: Team session streaming events are handled via teamSessionRegistry
  // (registered in handleSend when an @agent call is initiated)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (atBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeAgentCalls, atBottom]);

  // Scroll detection
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;
    setAtBottom(isAtBottom);

    // Trigger load more when near top
    if (scrollTop < 80 && hasMore && !isLoading) {
      void loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  const handleFilesSelected = useCallback(async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    if (fileList.length === 0) {
      return;
    }

    if (pendingAttachments.length >= MAX_CHAT_ATTACHMENTS) {
      window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in one message.`);
      return;
    }

    const nextAttachments: PendingAttachment[] = [];
    for (const file of fileList) {
      if (pendingAttachments.length + nextAttachments.length >= MAX_CHAT_ATTACHMENTS) {
        window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in one message.`);
        break;
      }

      try {
        nextAttachments.push(await createPendingAttachment(file));
      } catch (error) {
        console.error('[TeamChannelView] Failed to prepare file for upload:', error);
        window.alert("This file type isn't supported.");
      }
    }

    if (nextAttachments.length > 0) {
      setPendingAttachments((prev) => [...prev, ...nextAttachments]);
    }
  }, [pendingAttachments.length]);

  const clearTokenWarning = useCallback(() => {
    setPendingTokenWarningText(null);
    setPendingTokenWarningCount(null);
  }, []);

  useEffect(() => {
    clearTokenWarning();
  }, [activeSessionKey, clearTokenWarning]);

  const maybeOpenTokenWarning = useCallback((messageText: string, hasAttachments: boolean) => {
    const tokenCount = getSessionTokenCount(sessions, activeSessionKey);
    if (!shouldShowContextTokenWarning({ messageText, hasAttachments, tokenCount })) {
      return false;
    }

    setPendingTokenWarningText(messageText);
    setPendingTokenWarningCount(tokenCount);
    return true;
  }, [activeSessionKey, sessions]);

  const handleSend = useCallback(async (skipTokenWarning = false) => {
    const text = inputValue.trim();
    const queuedAttachments = pendingAttachments;
    if ((!text && queuedAttachments.length === 0) || isSending) return;

    if (!skipTokenWarning && maybeOpenTokenWarning(text, queuedAttachments.length > 0)) {
      return;
    }

    setIsSending(true);

    try {
      const gatewayToken = useAppStore.getState().gatewayToken;
      const uploadedAttachments = queuedAttachments.length > 0
        ? await uploadPendingAttachments(queuedAttachments, gatewayToken)
        : [];

      // Check for @mention
      const mentionMatch = text.match(/^@(\S+)(?:\s+([\s\S]+))?$/);
      if (mentionMatch) {
        const [, mentionedName, questionText = ''] = mentionMatch;
        const matchedAgent = agents.find(
          (a) =>
            (a.identity?.name ?? a.name ?? a.id).toLowerCase() === mentionedName.toLowerCase()
        );

        if (matchedAgent && (questionText.trim() || uploadedAttachments.length > 0)) {
          const agentId = matchedAgent.id;
          const agentName = matchedAgent.identity?.name ?? matchedAgent.name ?? agentId;
          const question = appendUploadedAttachmentsToMessage(questionText, uploadedAttachments);

          // Get context from current messages
          const { messages: currentMessages, contextMessageLimit } = useTeamChannelStore.getState();
          const contextMsgs = currentMessages.slice(-contextMessageLimit);
          const contextLines = contextMsgs
            .filter((m) => m.type === 'human' || m.type === 'agent_call')
            .map((m) => {
              const timeStr = new Date(m.ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              return `[${m.sender} ${timeStr}]: ${m.content}`;
            })
            .join('\n');

          const senderName = useSettingsStore.getState().settings.general.displayName.trim() ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('clawlet.displayName') : null) ||
            'User';
          const formattedMessage = contextLines
            ? `[Team Channel — recent context]\n${contextLines}\n\n[${senderName}'s question to you]:\n${question}`
            : question;

          const { sendAgentCall, saveAgentReply, setActiveAgentCallStreaming, setActiveAgentCallDone, setActiveAgentCallThinking, setActiveAgentCallToolCall } =
            useTeamChannelStore.getState();

          const { callId, sessionKey } = await sendAgentCall(agentId, agentName, question);

          // eslint-disable-next-line prefer-const
          let agentCallTimeoutId: ReturnType<typeof setTimeout>;

          registerTeamSession(sessionKey, {
            callId,
            agentId,
            agentName,
            onText: (streamingText) => {
              setActiveAgentCallStreaming(callId, streamingText);
            },
            onDone: (finalText) => {
              clearTimeout(agentCallTimeoutId);
              setActiveAgentCallDone(callId);
              void saveAgentReply(callId, agentId, agentName, sessionKey, finalText);
              unregisterTeamSession(sessionKey);
            },
            onThinking: (thinkingText) => {
              setActiveAgentCallThinking(callId, thinkingText);
            },
            onToolCall: (toolCall) => {
              setActiveAgentCallToolCall(callId, toolCall);
            },
          });

          agentCallTimeoutId = setTimeout(() => {
            const { activeAgentCalls } = useTeamChannelStore.getState();
            if (activeAgentCalls[callId]) {
              console.warn('[TeamChannelView] Agent call timed out after 120s:', sessionKey);
              setActiveAgentCallDone(callId);
              unregisterTeamSession(sessionKey);
            }
          }, 120_000);

          const client = gatewayRef.current;
          if (!client) {
            console.error('[TeamChannelView] Gateway not connected');
            setActiveAgentCallDone(callId);
            unregisterTeamSession(sessionKey);
          } else {
            try {
              // Pre-configure session with reasoning + verbose before sending
              await fetch(buildApiUrl('/api/team/enable-reasoning', useAppStore.getState().gatewayUrl), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${useAppStore.getState().gatewayToken}` },
                body: JSON.stringify({ sessionKey }),
              }).catch((e: unknown) => console.debug('[TeamChannelView] pre-configure err:', e));

              await client.sendMessage(sessionKey, formattedMessage);
            } catch (err) {
              console.error('[TeamChannelView] Gateway send error:', err);
              setActiveAgentCallDone(callId);
              unregisterTeamSession(sessionKey);
              throw err;
            }
          }

          setInputValue('');
          setPendingAttachments([]);
          return;
        }
      }

      const messageText = appendUploadedAttachmentsToMessage(text, uploadedAttachments);
      await sendMessage(messageText);
      setInputValue('');
      setPendingAttachments([]);
    } catch (err) {
      console.error('[TeamChannelView] handleSend error:', err);
      window.alert(toFriendlyErrorMessage(err, 'chat-send'));
    } finally {
      setIsSending(false);
    }
  }, [activeSessionKey, agents, inputValue, isSending, maybeOpenTokenWarning, pendingAttachments, sendMessage, sessions, setInputValue]);

  const handleProceedTokenWarning = () => {
    if (pendingTokenWarningText === null) {
      return;
    }

    clearTokenWarning();
    void handleSend(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleAgentIconClick = (agentName: string) => {
    setInputValue(`@${agentName} `);
    inputRef.current?.focus();
  };

  const handleOpenPopup = (message: TeamMessage) => {
    if (!message.sessionKey) return;
    openPopup(message.sessionKey, message.agentName ?? message.sender, message.id);
  };

  // Build member/agent count
  const uniqueSenders = new Set(messages.filter((m) => m.type === 'human' || m.type === 'agent_call').map((m) => m.sender));
  const memberCount = Math.max(uniqueSenders.size, 1);
  const agentCount = agents.filter((a) => a.identity != null).length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--theme-background)',
        overflow: 'hidden',
      }}
    >
      {/* Header — Fix 2: compact, with status merged in */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.45rem 1rem',
          borderBottom: '1px solid var(--theme-border)',
          flexShrink: 0,
          background: 'var(--theme-card)',
        }}
      >
        {/* Left: green pulse dot + channel name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: 'var(--theme-success)',
              boxShadow: '0 0 6px color-mix(in srgb, var(--theme-success) 50%, transparent)',
              animation: 'mintPulse 2s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'var(--theme-foreground)',
            }}
          >
            # team
          </div>
        </div>

        {/* Right: view toggle + status text + gear */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            gap: '2px',
            background: 'var(--theme-secondary)',
            padding: '3px',
            borderRadius: '6px',
          }}>
            <button
              type="button"
              onClick={() => setViewMode('normal')}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                border: 'none',
                background: viewMode === 'normal' ? 'var(--theme-card)' : 'transparent',
                color: viewMode === 'normal' ? 'var(--theme-foreground)' : 'var(--theme-muted-foreground)',
                cursor: 'pointer',
                fontFamily: 'var(--app-ui-font)',
                fontSize: '0.55rem',
                fontWeight: 600,
                transition: 'all 0.15s ease',
                boxShadow: viewMode === 'normal' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              💬 Chat
            </button>
            <button
              type="button"
              onClick={() => setViewMode('meeting')}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                border: 'none',
                background: viewMode === 'meeting' ? 'var(--theme-card)' : 'transparent',
                color: viewMode === 'meeting' ? 'var(--theme-foreground)' : 'var(--theme-muted-foreground)',
                cursor: 'pointer',
                fontFamily: 'var(--app-ui-font)',
                fontSize: '0.55rem',
                fontWeight: 600,
                transition: 'all 0.15s ease',
                boxShadow: viewMode === 'meeting' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              🎯 Meeting
            </button>
          </div>
          <span
            style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.55rem',
              color: 'var(--theme-muted-foreground)',
              letterSpacing: '0.03em',
            }}
          >
            {memberCount} {memberCount === 1 ? 'member' : 'members'}, {agentCount} {agentCount === 1 ? 'agent' : 'agents'} — all questions answered
          </span>
          <button
            type="button"
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '5px',
              border: '1px solid var(--theme-border)',
              background: 'transparent',
              color: 'var(--theme-muted-foreground)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
            }}
            title="Settings (coming soon)"
          >
            ⚙
          </button>
        </div>
      </div>

      {/* Meeting view — full replacement when toggled */}
      {viewMode === 'meeting' ? (
        <AgentMeetingView />
      ) : (
      <>

      {/* Agent icon bar */}
      <AgentIconBar
        agents={agents}
        inputValue={inputValue}
        onAgentClick={handleAgentIconClick}
      />

      {/* Message list */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="team-channel-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.375rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.875rem',
        }}
      >
        {/* Load more / beginning */}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0.75rem',
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              color: 'var(--theme-muted-foreground)',
            }}
          >
            Loading...
          </div>
        )}

        {!hasMore && !isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0.75rem',
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              color: 'var(--theme-muted-foreground)',
              letterSpacing: '0.06em',
            }}
          >
            ─── Beginning of channel ───
          </div>
        )}

        {messages.length === 0 && !isLoading && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: '0.75rem',
              padding: '3rem',
            }}
          >
            <div style={{ fontSize: '2rem' }}>💬</div>
            <div
              style={{
                fontFamily: 'var(--app-ui-font)',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'var(--theme-foreground)',
                letterSpacing: '0.04em',
              }}
            >
              Team channel is empty
            </div>
            <div
              style={{
                fontFamily: 'var(--app-ui-font)',
                fontSize: '0.65rem',
                color: 'var(--theme-muted-foreground)',
                textAlign: 'center',
                maxWidth: '320px',
                lineHeight: 1.6,
              }}
            >
              Send a message or @mention an agent to get started.
            </div>
          </div>
        )}

        {/* Render messages */}
        {messages.map((msg, i) => {
          const prev = messages[i - 1];

          if (msg.type === 'agent_reply') {
            return (
              <AgentReplyRow
                key={msg.id}
                message={msg}
                maxLength={agentReplyMaxLength}
                onExpand={() => handleOpenPopup(msg)}
              />
            );
          }

          return (
            <HumanMessage
              key={msg.id}
              message={msg}
              prevMessage={prev}
              maxLength={agentReplyMaxLength}
            />
          );
        })}

        {/* Active agent streaming indicators */}
        {Object.entries(activeAgentCalls).map(([callId, callState]) => {
          // Find agent info
          const agentId = callState.sessionKey.split(':')[1] ?? 'main';
          const agentInfo = agents.find((a) => a.id === agentId);
          const agentName = agentInfo?.identity?.name ?? agentInfo?.name ?? 'Agent';
          const agentEmoji = getAgentEmoji(agentId);

          // Map ActiveAgentCallToolCall → ToolCallEntry for ToolCallSection
          const toolCallEntries: ToolCallEntry[] = (callState.toolCalls ?? []).map((tc) => {
            // Parse JSON-stringified params/result for richer display
            let params: Record<string, unknown> | undefined;
            if (tc.input) {
              try { params = JSON.parse(tc.input); } catch { params = { input: tc.input }; }
            }
            let result: unknown;
            if (tc.output) {
              try { result = JSON.parse(tc.output); } catch { result = tc.output; }
            }
            return {
              toolCallId: tc.toolCallId,
              name: tc.name,
              timestamp: Date.now(),
              status: tc.status,
              params,
              result,
            };
          });

          return (
            <AgentStreamingRow
              key={callId}
              agentName={agentName}
              agentEmoji={agentEmoji}
              streamText={callState.streamText}
              maxLength={agentReplyMaxLength}
              thinkingText={callState.thinkingText}
              toolCalls={toolCallEntries}
            />
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar (no status footer — Fix 2: merged into header) */}
      <div
        style={{
          padding: '0.875rem 1.375rem',
          borderTop: '1px solid var(--theme-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          flexShrink: 0,
          background: 'var(--theme-background)',
        }}
      >
        {pendingAttachments.length > 0 ? (
          <AttachmentPreviewBar
            attachments={pendingAttachments}
            onRemove={(id) => setPendingAttachments((prev) => prev.filter((attachment) => attachment.id !== id))}
          />
        ) : null}

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={CHAT_UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              if (event.target.files) {
                void handleFilesSelected(event.target.files);
              }
              event.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS}
            title="Attach file"
            style={{
              height: '36px',
              width: '36px',
              borderRadius: '6px',
              border: '1px solid var(--theme-border)',
              background: 'var(--theme-card)',
              color: 'var(--theme-foreground)',
              fontSize: '1rem',
              cursor: 'pointer',
              flexShrink: 0,
              opacity: isSending || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS ? 0.4 : 1,
            }}
          >
            📎
          </button>
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message team... or @AgentName to ask an agent"
            disabled={isSending}
            rows={1}
            style={{
              flex: 1,
              background: 'var(--theme-input)',
              border: '1px solid var(--theme-border)',
              borderRadius: '6px',
              padding: '0.6rem 0.875rem',
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.72rem',
              color: 'var(--theme-foreground)',
              resize: 'none',
              outline: 'none',
              lineHeight: 1.5,
              minHeight: '36px',
              maxHeight: '120px',
              overflowY: 'auto',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'color-mix(in srgb, var(--theme-primary) 30%, transparent)'; }}
            onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'var(--theme-border)'; }}
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={(!inputValue.trim() && pendingAttachments.length === 0) || isSending}
            style={{
              height: '36px',
              padding: '0 1rem',
              borderRadius: '6px',
              border: 'none',
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-foreground)',
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
              flexShrink: 0,
              opacity: (!inputValue.trim() && pendingAttachments.length === 0) || isSending ? 0.4 : 1,
            }}
          >
            SEND ↵
          </button>
        </div>
      </div>

      </>
      )}

      {/* Popup modal */}
      {popupSessionKey && <AgentReplyPopup />}

      <ContextTokenWarningDialog
        tokenCount={pendingTokenWarningCount}
        onCancel={clearTokenWarning}
        onProceed={handleProceedTokenWarning}
      />
    </div>
  );
}
